// useNotificationPersonalizationMap.ts
import { useCallback, useMemo } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import type { IconName } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { WeavePopNotification } from '@frontend/types';
import { usePreference } from '@frontend/user-preferences';
import { colorMap, type ColorMap } from './color-map';

type ColorType = keyof typeof colorMap;

// Type for notification entry
type NotificationStyle = {
  color: ColorType;
  icon: Extract<IconName, `${string}-small`>;
};

type ColorNotificationTypeMap = Record<WeavePopNotification['type'], NotificationStyle>;
export const useNotificationPersonalizationMap = (): ((type: WeavePopNotification['type']) => {
  color: ColorMap[ColorType];
  icon: NotificationStyle['icon'];
}) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showColorChipForYou = getFeatureFlagValue('for-you-colors-chip');

  const [forYouNotificationColors] = usePreference('ForYouNotificationColors');
  const getNotificationColor = useCallback(
    (notificationType: NotificationType | 'Tags', fallbackColor: ColorType): ColorType => {
      return showColorChipForYou ? (forYouNotificationColors[notificationType] as ColorType) : fallbackColor;
    },
    [forYouNotificationColors, showColorChipForYou]
  );

  const colorNotificationTypeMap: ColorNotificationTypeMap = useMemo(() => {
    return {
      'device-connection': {
        color: getNotificationColor(NotificationType.DEVICE_CONNECTION, 'primary'),
        icon: 'info-small',
      },
      'sms-message-new': {
        color: getNotificationColor(NotificationType.SMS, 'tangerine'),
        icon: 'message-unread-small',
      },
      'fax-new': {
        color: getNotificationColor(NotificationType.FAX, 'warning'),
        icon: 'fax-small',
      },
      'fax-tag': {
        color: getNotificationColor('Tags', 'critical'),
        icon: 'fax-small',
      },
      'call-record-tag': {
        color: getNotificationColor('Tags', 'critical'),
        icon: 'label-small',
      },
      'chat-message-new': {
        color: getNotificationColor(NotificationType.CHAT_MESSAGE, 'indigo'),
        icon: 'chat-small',
      },
      'review-new': {
        color: getNotificationColor(NotificationType.REVIEWS, 'neutral'),
        icon: 'star-small',
      },
      'schedule-request-new': {
        color: getNotificationColor(NotificationType.SCHEDULE_REQUEST, 'rose'),
        icon: 'calendar-small',
      },
      'schedule-waitlist': {
        color: getNotificationColor(NotificationType.SCHEDULE_REQUEST, 'rose'),
        icon: 'calendar-small',
      },
      'missed-call': {
        color: 'eggplant',
        icon: 'phone-missed-small',
      },
      'incoming-call': {
        color: 'eggplant',
        icon: 'phone-incoming-small',
      },
      'form-submission-new': {
        color: getNotificationColor(NotificationType.FORMS, 'primary'),
        icon: 'forms-small',
      },
      payments: {
        color: getNotificationColor(NotificationType.PAYMENTS, 'seaweed'),
        icon: 'payments-small',
      },
      'task-center': {
        color: getNotificationColor(NotificationType.TASK, 'indigo'),
        icon: 'task-small',
      },
      'task-center-ai-settings': {
        color: getNotificationColor(NotificationType.TASK_CENTER_AI_AUTO_ASSIGN, 'indigo'),
        icon: 'task-small',
      },
      'voicemail-message': {
        color: getNotificationColor(NotificationType.VOICEMAIL_MESSAGE, 'eggplant'),
        icon: 'voicemail-small',
      },
      'voicemail-tag': {
        color: getNotificationColor('Tags', 'critical'),
        icon: 'label-small',
      },
      'message-followup': {
        color: 'indigo',
        icon: 'message-small',
      },
      'sms-tag': {
        color: getNotificationColor('Tags', 'critical'),
        icon: 'label-small',
      },
      'inbox-robocall-tag': {
        color: getNotificationColor('Tags', 'critical'),
        icon: 'label-small',
      },
      'unhappy-call': {
        color: 'gradient',
        icon: 'call-intel-small',
      },
      preference: {
        color: 'neutral',
        icon: 'settings-small',
      },
    };
  }, [forYouNotificationColors, showColorChipForYou]);

  const personalizationMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(colorNotificationTypeMap).map(([type, { color, icon }]) => [
          type,
          {
            color: colorMap[color],
            icon,
          },
        ])
      ) as Record<
        WeavePopNotification['type'],
        {
          color: ColorMap[ColorType];
          icon: NotificationStyle['icon'];
        }
      >,
    [colorNotificationTypeMap]
  );

  return useCallback(
    (type: WeavePopNotification['type']) =>
      personalizationMap[type] ?? {
        color: colorMap['primary'], // fallback color
        icon: 'info-small', // fallback icon
      },
    [personalizationMap]
  );
};
